import { useQuery } from '@tanstack/react-query';
import { formatFormikError } from 'fetcher-session';
import { Form, Formik } from 'formik';
import {
  FormikCheckbox,
  FormikErrorMessage,
  FormikField,
  FormikSelectField,
} from 'formik-ui';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import {
  fetchAddresses,
  fetchRegions,
  postCreateAddress,
  putUpdateAddress,
} from '~/api/address';
import FormikDynamicStateSelect from '~/components/Address/FormikDynamicStateSelect';
import FormikAddressAutocompleteField from '~/components/Form/FormikAddressAutocompleteField';
import { RailsAddress } from '~/typings/services/rails/session';

const schema = yup.object().shape({
  street_1: yup.string().required('Required'),
  city: yup.string().required('Required'),
  state: yup.string().required('Required'),
  country: yup.string().required('Required'),
  zip: yup.string().required('Required'),
});

export interface AddressFormValues {
  first_name: string;
  last_name: string;
  street_1: string;
  street_2: string | undefined;
  city: string;
  state: string;
  country: string;
  zip: string;
  flags: {
    legal: boolean;
    ship_from: boolean;
    ship_to: boolean;
  };
}

type Props = {
  actions: React.ReactNode;
  defaultFlags?: Partial<AddressFormValues['flags']>;
  editAddress?: RailsAddress;
  neverShowCheckboxes?: boolean;
  onSuccessfulSubmit?: (address: RailsAddress) => void;
};

const AddressForm: React.FC<Props> = ({
  actions,
  defaultFlags,
  editAddress,
  neverShowCheckboxes,
  onSuccessfulSubmit,
}) => {
  const { data: addresses } = useQuery(['addresses'], fetchAddresses, {
    staleTime: 60000,
  });
  const { data: regions } = useQuery(['regions'], fetchRegions, {
    staleTime: Infinity,
  });

  const sortedRegions = useMemo(
    () => regions?.sort((a, b) => a.name.localeCompare(b.name)),
    [regions],
  );

  return (
    <Formik
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={schema}
      onSubmit={async (values, formikHelpers) => {
        formikHelpers.setSubmitting(true);
        try {
          let address: RailsAddress;
          if (editAddress) {
            address = await putUpdateAddress(editAddress.id, values);
          } else {
            address = await postCreateAddress({
              ...values,
            });
          }

          onSuccessfulSubmit?.(address);
        } catch (err) {
          formikHelpers.setErrors(formatFormikError(err));
          formikHelpers.setSubmitting(false);
        }
      }}
      initialValues={
        {
          first_name: editAddress?.first_name || '',
          last_name: editAddress?.last_name || '',
          street_1: editAddress?.street_1 || '',
          street_2: editAddress?.street_2 || '',
          city: editAddress?.city || '',
          state: editAddress?.state || '',
          country: editAddress?.country || 'US',
          zip: editAddress?.zip || '',
          flags: {
            legal: editAddress
              ? editAddress.flags.legal
              : defaultFlags?.legal ?? true,
            ship_from: editAddress
              ? editAddress.flags.ship_from
              : defaultFlags?.ship_from ?? true,
            ship_to: editAddress
              ? editAddress.flags.ship_to
              : defaultFlags?.ship_to ?? true,
          },
        } as AddressFormValues
      }
    >
      {({ values }) => {
        const showCheckboxes =
          !neverShowCheckboxes &&
          (editAddress ||
            addresses?.length! > 0 ||
            (values.country
              ? values.country !== 'US' && values.country !== 'CA'
              : false));

        return (
          <Form id="address-form">
            <FormikSelectField label="Country" name="country">
              {sortedRegions?.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </FormikSelectField>

            <div className="grid grid-cols-2 gap-4">
              <FormikField label="First Name" name="first_name" autoFocus />
              <FormikField label="Last Name" name="last_name" />
            </div>

            <FormikAddressAutocompleteField label="Address 1" name="street_1" />

            <FormikField label="Address 2 (optional)" name="street_2" />

            <div className="grid grid-cols-3 gap-4">
              <FormikField label="City" name="city" />

              <FormikDynamicStateSelect
                countryFieldName="country"
                name="state"
                label="State"
              />

              <FormikField
                label={values.country === 'US' ? 'Zip Code' : 'Postal Code'}
                name="zip"
              />
            </div>

            {showCheckboxes && (
              <div className="mb-4 space-y-2">
                <FormikCheckbox name="flags.legal">
                  Set as legal address
                </FormikCheckbox>

                <FormikCheckbox name="flags.ship_from">
                  Set as ship from address
                </FormikCheckbox>

                <FormikCheckbox name="flags.ship_to">
                  Set as ship to address
                </FormikCheckbox>
              </div>
            )}

            <FormikErrorMessage />

            {actions}
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddressForm;
